import React, { useEffect, useState, useRef } from "react";
import ReactToPrint from 'react-to-print';
// Chakra imports
import {
  Box,
  Button,
  Flex,
  Grid,
  Icon,
  Image,
  Portal,
  Progress,
  SimpleGrid,
  Spacer,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Table,
  Thead,
  Tbody,
  Tfoot,
  Text,
  Th,
  Tr,
  Td,
  useColorMode,
  useColorModeValue,
  useDisclosure,
  FormControl,
  FormLabel,
  Select,
  VStack,
  Menu,
  MenuButton,
  MenuList,
  Switch,
  HStack,
} from "@chakra-ui/react";
// assets
import peopleImage from "assets/img/people-image.png";
import logoChakra from "assets/svg/logo-white.svg";
// Custom components
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";
import BarChart from "components/Charts/BarChart";
import LineChart from "components/Charts/LineChart";
import IconBox from "components/Icons/IconBox";
// Custom icons
import {
    CartIcon,
    DocumentIcon,
    GlobeIcon,
    RocketIcon,
    StatsIcon,
    WalletIcon,
    PersonIcon
} from "components/Icons/Icons.js";
import { SettingsIcon } from "@chakra-ui/icons";
import EasyChekLogo from "assets/images/easychek_logo.jpg";
import "assets/css/print.css";
import "assets/css/easychek.css";
import useGetProducts from "hooks/useGetProducts";

import { MySearchBar } from "views/Custom/MySearchBar";

// react icons
export default function InventoryReports() {
    let navbarIcon = useColorModeValue("gray.500", "gray.200");
    const [ keyword, setKeyword ] = useState("");
    const [ supplierKeyword, setSupplierKeyword ] = useState("");
    const [ withStocks, setWithStocks ] = useState(null);

    let { 
        products, 
        totalProducts, 
        setGettingProducts, 
        gettingProducts, 
        gettingProductsReqSuccess 
    } = useGetProducts({ 
        page: 1, 
        limit: 5000,
        keyword: keyword != "" ? keyword : undefined, 
        supplierKeyword: supplierKeyword != "" ? supplierKeyword : undefined, 
        withStocks: withStocks,
    });
    const [ filter, setFilter ] = useState('all');
    const [ shownProducts, setShownProducts ] = useState([]);
    const [ supplierList, setSupplierList ] = useState([]);
    const [ computedUnitPrice, setComputedUnitPrice ] = useState(0);
    const [ computedSellingPrice, setComputedSellingPrice ] = useState(0);


    const value = "$100.000";
    // Chakra Color Mode
    const { colorMode, toggleColorMode } = useColorMode();
    const iconTeal = useColorModeValue("teal.300", "teal.300");
    const iconBoxInside = useColorModeValue("white", "white");
    const textColor = useColorModeValue("gray.700", "white");
    
    const printableComponentRef = useRef();
    const [ textSize, setTextSize ] = useState("x-small");
    const [ visibleColumns, setVisibleColumns ] = useState({
        "Name": true,
        "Description": true,
        "Vehicle": true, 
        "Supplier": true,
        "Quantity": true,
        "Unit Price": true,
        "Selling Price": true,
    });
    

    useEffect(() => {

      const sList = ['all'];
      // console.log(products);
      products.forEach((v) => {
          v.stocks.forEach((s) => {
              if (!sList.includes(s.supplier.toString().toLowerCase())) {
                  sList.push(s.supplier.toString().toLowerCase());
              }
          })
      });

      const pList = [];
      // console.log(products);
      products.forEach((v) => {
          if (filter.toLowerCase() == 'all') {
              pList.push(v);
          } else {
              const tmpProduct = {
                ...v,
                stocks: [],
                totalStocks: 0,
              };

              v.stocks.forEach((s) => {
                  if (s.supplier.toString().toLowerCase() == filter) {
                      tmpProduct.stocks.push(s);
                      tmpProduct.totalStocks += parseInt(s.quantity);
                  }
              }) 

              if (tmpProduct.stocks.length > 0) {
                  pList.push(tmpProduct);
              }
          }
      });


      let totalUnitPrice = 0, totalSellingPrice = 0;
      pList.forEach((v) => {
          v.stocks.forEach((s) => {
              totalUnitPrice += parseInt(s.quantity) * parseFloat(s.unitPrice);
              totalSellingPrice += parseInt(s.quantity) * parseFloat(s.sellingPrice);
          })
      });


      setShownProducts(pList);
      setSupplierList(sList);
      setComputedUnitPrice(totalUnitPrice);
      setComputedSellingPrice(totalSellingPrice);

    }, [products, filter]);



    const onChangeVisibleColumn = (columnName, value) => {
        const tmp = {...visibleColumns};
        tmp[columnName] = value;
        setVisibleColumns(tmp);
      // console.log(columnName, value);
    }

    const getTotalColSpan = () => {
        let cspan = 0;
        if (visibleColumns["Name"]) cspan++;
        if (visibleColumns["Description"]) cspan++;
        if (visibleColumns["Vehicle"]) cspan++;
        if (visibleColumns["Supplier"]) cspan++;
        if (visibleColumns["Quantity"]) cspan++;
        return cspan;
    }


    return (
      <Flex flexDirection="column" pt={{ base: "120px", md: "75px" }}>
        
          <Grid
              templateColumns={{ md: "3fr", lg: "3fr" }}
              templateRows={{ md: "1fr auto", lg: "1fr" }}
              my="12px"
              gap="24px"
          >

            <Card 
                p="1.2rem" 
                w="100%"
                h={{ sm: "auto", lg: "auto" }}
                position="relative"
                borderRadius="15px"
            >
                <CardBody w="100%">
                    <Flex flexDirection="row" width="100%">

                          <MySearchBar 
                              keyword={keyword}
                              setKeyword={setKeyword}
                              // onReload={reloadPage}
                              hint="By Item" 
                          />
                          <Box marginLeft={4}/>

                          <MySearchBar 
                              keyword={supplierKeyword}
                              setKeyword={setSupplierKeyword}
                              // onReload={reloadPage}
                              hint="By Supplier" 
                          />
                          <Box marginLeft={4}/>

                          {/* With or Without stocks */}
                          <HStack alignItems="top">
                              <Select 
                                  defaultValue={'All'}
                                  onChange={(e) => {
                                      if (e.target.value == 'All') {
                                        setWithStocks(null);
                                      } else if (e.target.value == 'WithStocks') {
                                        setWithStocks(true);
                                      } else if (e.target.value == 'WithOutStocks') {
                                        setWithStocks(false);
                                      }
                                  }}
                              >
                                  <option key="stocks-all" value="All">All</option>
                                  <option key="stocks-with" value="WithStocks">With Stocks</option>
                                  <option key="stocks-without" value="WithOutStocks">Without Stocks</option>
                              </Select>
                          </HStack>


                          <FormControl>
                            {/* <FormLabel>&nbsp;</FormLabel> */}
                            <Button
                                fontSize="10px"
                                type="submit"
                                bg="teal.300"
                                w="100%"
                                maxWidth="160px"
                                // h="45"
                                color="white"
                                marginLeft={4}
                                _hover={{
                                bg: "teal.200",
                                }}
                                _active={{
                                bg: "teal.400",
                                }}
                                isDisabled={ gettingProducts } 
                                onClick={() => setGettingProducts(true)}
                            >
                                RELOAD
                            </Button>
                          </FormControl>

                    </Flex>

                </CardBody>
            </Card>


          </Grid>


          <Grid
            templateColumns={{ md: "3fr", lg: "3fr" }}
            templateRows={{ md: "1fr auto", lg: "1fr" }}
            my="12px"
            gap="24px"
          >

            <Card p="1.2rem" 
              w="100%"
              h={{ sm: "auto", lg: "auto" }}
              position="relative"
              borderRadius="15px">
              <CardBody w="100%">
                <Flex flexDirection="column" width="100%">
                    
                  <Flex flexDirection="row" width="100%" flex>

                    <HStack ml={2} flex={1} flexGrow={1} flexBasis={0}>
                      <Text marginLeft={2}>Filter</Text>
                      <Select maxWidth={240}
                        defaultValue={"all"}
                        onChange={(e) =>  setFilter(e.target.value)}
                      >
                        {/* <option value="all">All</option> */}
                        { supplierList.map((s) => {
                            return <option 
                              value={s}
                              style={{
                                textTransform: 'capitalize'
                              }}>{s}</option>
                          })
                        }
                      </Select>
                    </HStack>
                    
                    <Box textAlign="center" flex={1} flexGrow={1} flexBasis={0}>
                      <ReactToPrint
                        trigger={() => <Button
                          maxWidth="120px"
                          fontSize="10px"
                          type="submit"
                          bg="teal.300"
                          color="white"
                          marginLeft={4}
                          _hover={{
                          bg: "teal.200",
                          }}
                          _active={{
                          bg: "teal.400",
                          }}
                          disabled={ shownProducts.length == 0 }
                          >Print this out!</Button>}
                          content={() => printableComponentRef.current}
                      />
                    </Box>


                    <Box textAlign="right" flex={1} flexGrow={1} flexBasis={0}>
                      <Menu flex={1} >
                        <MenuButton>
                          <SettingsIcon color={navbarIcon} w="24px" h="24px" />
                        </MenuButton>
                        <MenuList p="16px 8px" width="80px">
                          <Flex flexDirection="column">
                            { Object.keys(visibleColumns).map((key) => {
                                return (
                                  <FormControl display="flex" key={`control-key-${key}`}
                                    alignItems="center" >
                                    <FormLabel htmlFor={key} mb="0"
                                    width="100%">
                                      {key}
                                    </FormLabel>
                                    <Switch id={key} defaultChecked={visibleColumns[key]}
                                      onChange={e => onChangeVisibleColumn(key, e.target.checked)}/>
                                  </FormControl>
                                )
                              })
                            }
                            <FormControl display="flex" marginTop="4px">
                              <FormLabel flex={1}>Font Size</FormLabel>
                              <Select defaultValue={textSize} fontSize="xs" 
                              maxWidth={120}
                                onChange={(e) => setTextSize(e.target.value)}>
                                  <option value="small">Normal</option>
                                  <option value="x-small">Small</option>
                                  <option value="xx-small">X-Small</option>
                              </Select>
                            </FormControl>
                          </Flex>
                        </MenuList>
                      </Menu>
                    </Box>

                  </Flex>
                  
            
                  <Box width={"100%"} 
                      // direction="column"
                      alignSelf="center"
                      justifySelf="center"
                  >
                      <VStack >
                        <Flex
                          flex={1}
                          direction="column"
                          alignSelf="center"
                          justifySelf="center"
                          justifyContent="center"
                          width={"99%"} 
                          m="4px"
                          mt="10px"
                          ref={printableComponentRef}>
                          {/** Logo and Header */}
                          <Flex 
                              direction="row"
                              textAlign="center"
                              justifyContent="center"
                              align="center"
                              mt="4px"
                              mb="12px"
                              display="none"
                              className="show-print"
                          >
                              <Image
                                // boxSize="100px"
                                width={"120px"}
                                objectFit="fit"
                                src={EasyChekLogo}
                                alt="Easy Chek 7 Logo"
                              />
                              <Flex direction={"column"}>
                                  <Text 
                                      fontSize="22px"
                                      color={"black"}
                                      fontWeight="bold"
                                      textAlign="center"
                                  >
                                      EASY CHEK 7 AUTO SERVICE CORP.
                                  </Text>
                                  <Text
                                      fontSize="xs"
                                      textAlign="center"
                                  >
                                      14-5 FIL AM ROAD CUTCUT ANGELES CITY PAMPANGA
                                  </Text>
                                  <Text
                                      fontSize="xs"
                                      textAlign="center"
                                  >
                                      CONTACT NO. 0917631-8628(GLOBE) AND 0943-706-0855(SMART)
                                  </Text>
                              </Flex>
                          </Flex>

                          <style type="text/css" media="print">{"\
                            @page {\ size: A4 landscape;\ }\
                          "}</style>

                          <Box 
                            display="block"
                            overflowX="auto">

                            <Table variant="simple" 
                              color={textColor} 
                              className="table-print"
                            >
                              <Thead>
                                <Tr my=".8rem" p={2} color="gray.400">
                                  {/* <Th p={2} color="gray.400" fontSize={textSize}>Name</Th>  */}
                                  { visibleColumns["Name"]
                                    ? <Th p={2} color="gray.400" fontSize={textSize}>NAME</Th>
                                    : null }
                                  { visibleColumns["Description"]
                                    ? <Th p={2} color="gray.400" fontSize={textSize}>DESCRIPTION</Th>
                                    : null }
                                  { visibleColumns["Vehicle"]
                                    ? <Th p={2} color="gray.400" fontSize={textSize}>VEHICLE</Th>
                                    : null }
                                  { visibleColumns["Supplier"]
                                    ? <Th p={2} color="gray.400" fontSize={textSize}>SUPPLIER</Th>
                                    : null }
                                  { visibleColumns["Quantity"]
                                    ? <Th p={2} color="gray.400" fontSize={textSize}>QUANTITY</Th>
                                    : null }
                                  { visibleColumns["Unit Price"]
                                    ? <Th p={2} color="gray.400" fontSize={textSize}>UNIT PRICE</Th>
                                    : null }
                                  { visibleColumns["Selling Price"]
                                    ? <Th p={2} color="gray.400" fontSize={textSize}>SELLING PRICE</Th>
                                    : null }
                                </Tr>
                              </Thead>
                              <Tbody>
                                {gettingProducts ? 
                                  <Tr my=".8rem">
                                    <Td minWidth={{ sm: "250px" }} p={2} colSpan={4}>
                                      <Progress isIndeterminate />
                                    </Td>
                                  </Tr>
                                  : shownProducts.length > 0 
                                    ? shownProducts.map((o, pIndex) => {
                                        return o.stocks.length == 0 
                                          ? <Tr my=".8rem" p={2} key={`tbl-row-${o.id}`}>
                                                { visibleColumns["Name"] 
                                                  ? <Td  p={2} fontSize={textSize}>{o.name}</Td>
                                                  : null 
                                                }
                                                { visibleColumns["Description"]
                                                  ?  <Td  p={2} fontSize={textSize}>{ o.description }</Td>
                                                  : null 
                                                } 
                                                { visibleColumns["Vehicle"] 
                                                  ? <Td  p={2} fontSize={textSize}>
                                                    {o.carMake ? o.carMake : ''} {o.carType ? ` ${o.carType}` : ''} 
                                                    {o.carYear ? ` (${o.carYear})` : ''} <br/>
                                                    {o.carPart ? `Part No: ${o.carPart}` : ''} 
                                                  </Td>
                                                  : null 
                                                } 
                                                { visibleColumns["Supplier"]
                                                  ? <Td  p={2} fontSize={textSize}></Td>
                                                  : null 
                                                } 
                                                { visibleColumns["Quantity"]
                                                  ? <Td  p={2} fontSize={textSize}></Td>
                                                  : null 
                                                } 
                                                { visibleColumns["Unit Price"]
                                                  ? <Td  p={2} fontSize={textSize}></Td>
                                                  : null 
                                                } 
                                                { visibleColumns["Selling Price"]
                                                  ? <Td  p={2} fontSize={textSize}></Td>
                                                  : null 
                                                } 
                                                
                                                {/* <Td  p={2}>{o.paymentsTotal.toLocaleString()}</Td>
                                                <Td  p={2}>{Number(parseFloat(o.total) - parseFloat(o.paymentsTotal)).toLocaleString()} </Td> */}
                                              
                                            </Tr>
                                          :  o.stocks.map((s, sIndex) => {
                                            return (
                                                <Tr my=".8rem" p={2} key={`tbl-row-${o.id}-${s.id}`}>
                                                  { visibleColumns["Name"] && sIndex == 0 
                                                    ? <Td  p={2} fontSize={textSize}
                                                      rowSpan={o.stocks.length}
                                                      verticalAlign="top">{o.name}</Td>
                                                    : null 
                                                  }
                                                  { visibleColumns["Description"] && sIndex == 0 
                                                    ?  <Td  p={2} fontSize={textSize}
                                                      rowSpan={o.stocks.length}
                                                      verticalAlign="top">{ o.description }</Td>
                                                    : null 
                                                  } 
                                                  { visibleColumns["Vehicle"] && sIndex == 0 
                                                    ? <Td  p={2} fontSize={textSize}
                                                      rowSpan={o.stocks.length}
                                                      verticalAlign="top">
                                                      {o.carMake ? o.carMake : ''} {o.carType ? ` ${o.carType}` : ''} 
                                                      {o.carYear ? ` (${o.carYear})` : ''} <br/>
                                                      {o.carPart ? `Part No: ${o.carPart}` : ''} 
                                                    </Td>
                                                    : null 
                                                  } 
                                                  { visibleColumns["Supplier"]
                                                    ? <Td  p={2} fontSize={textSize}>{s.supplier}</Td>
                                                    : null 
                                                  } 
                                                  { visibleColumns["Quantity"]
                                                    ? <Td  p={2} fontSize={textSize}>{s.quantity}</Td>
                                                    : null 
                                                  } 
                                                  { visibleColumns["Unit Price"]
                                                    ? <Td  p={2} fontSize={textSize}>{Number(s.unitPrice).toLocaleString()}</Td>
                                                    : null 
                                                  } 
                                                  { visibleColumns["Selling Price"]
                                                    ? <Td  p={2} fontSize={textSize}>{Number(s.sellingPrice).toLocaleString()}</Td>
                                                    : null 
                                                  } 
                                                  
                                                  {/* <Td  p={2}>{o.paymentsTotal.toLocaleString()}</Td>
                                                  <Td  p={2}>{Number(parseFloat(o.total) - parseFloat(o.paymentsTotal)).toLocaleString()} </Td> */}
                                                
                                                
                                                </Tr>
                                            );
                                          })
                                    })
                                    : <Tr my=".8rem" p={2}>
                                      <Td>No Result</Td>
                                    </Tr>
                                }
                              </Tbody>

                              { shownProducts.length > 0 
                                  ? <Tfoot>
                                      <Tr>
                                        <Td p={2} fontSize={textSize}
                                          textAlign="right"
                                          fontWeight="bold"
                                          colSpan={getTotalColSpan()}>TOTAL
                                        </Td>

                                        { visibleColumns["Unit Price"]
                                          ?  <Td  p={2} fontSize={textSize}>
                                              ₱{ Number(computedUnitPrice).toLocaleString() }
                                            </Td>
                                            : null 
                                        }
                                         

                                        { visibleColumns["Selling Price"]
                                          ?  <Td  p={2} fontSize={textSize}>
                                              ₱{ Number(computedSellingPrice).toLocaleString() }
                                            </Td>
                                            : null 
                                        }
                                        
                                      </Tr>
                                    </Tfoot>
                                  : null
                                }
                            </Table>
                          </Box>
                        
                        </Flex>
                      </VStack>
                  </Box>
                </Flex>
              </CardBody>
            </Card>

          </Grid>


      </Flex>
    );
}
